import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { useAuth } from './context/AuthContext';
import { clientService } from './services/api';
import Header from './components/Header';
import Footer from './components/Footer';
import DietRequests from './components/DietRequests';
import Trends from './components/Trends';
import Terms from './components/Terms';
import ProductUsageReport from './components/reports/ProductUsageReport';
import ContactRequests from './components/contact-requests/ContactRequests';
import ToastContainer from './components/common/ToastContainer';
import useNavbarTour from './components/navbar/useNavbarTour';
import 'flowbite';

// Inner component that uses useAuth hook
const AppContent = () => {
  const { isAuthenticated, isProcessingCallback, userInfo } = useAuth();
  const [isLoadingClientData, setIsLoadingClientData] = useState(true);
  const [clientData, setClientData] = useState(null);

  // Initialize navbar tour
  const { hasSeenNavbarTour } = useNavbarTour({ isAuthenticated, user: userInfo });

  // Fetch client data only for non-admin users
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        // Only fetch client data if user is not an admin
        if (userInfo?.role !== 'admin') {
          const data = await clientService.getClientData();
          setClientData(data);
        } else {
          // For admin users, set clientData to null
          setClientData(null);
        }
      } catch (error) {
        console.error('Error fetching client data:', error);
        setClientData(null);
      } finally {
        setIsLoadingClientData(false);
      }
    };

    // Only run when we have authentication info
    if (isAuthenticated && userInfo) {
      fetchClientData();
    } else if (isAuthenticated && !userInfo) {
      // If authenticated but no userInfo yet, keep loading
      return;
    } else {
      // Not authenticated, stop loading
      setIsLoadingClientData(false);
    }
  }, [isAuthenticated, userInfo]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header clientData={clientData} isLoadingClientData={isLoadingClientData} />
      <ToastContainer />
      <main className="flex-grow flex">
        <div className="flex-1 w-full">
          <Routes>
            {/* Public route */}
            <Route path="/terms" element={<Terms />} />

            {/* Protected routes */}
            {!isAuthenticated ? (
              <Route path="*" element={
                <div className="flex items-center justify-center flex-1 pt-32">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">
                      {isProcessingCallback ? 'Processing login...' : 'Redirecting to login...'}
                    </p>
                  </div>
                </div>
              } />
            ) : (
              <>
                <Route path="/" element={<Navigate to="/trends" replace />} />
                <Route path="/trends" element={<Trends hasSeenNavbarTour={hasSeenNavbarTour} />} />
                <Route path="/diet-requests" element={
                  <div className="max-w-screen-xl mx-auto">
                    <DietRequests hasSeenNavbarTour={hasSeenNavbarTour} />
                  </div>
                } />
                <Route path="/reports/product-usage" element={
                  <div className="max-w-screen-xl mx-auto">
                    <ProductUsageReport hasSeenNavbarTour={hasSeenNavbarTour} />
                  </div>
                } />
                <Route path="/contact-requests" element={
                  <div className="max-w-screen-xl mx-auto">
                    <ContactRequests hasSeenNavbarTour={hasSeenNavbarTour} />
                  </div>
                } />
              </>
            )}
          </Routes>
        </div>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
